import type { Schemas } from "../../api/client.ts";
import { useSession } from "../../api/hooks.ts";
import { useOwnerId } from "./owner.ts";

/**
 * Quien esta dentro y que puede hacer, leido de la sesion (ADR 0005).
 *
 * ====================================================================
 * ESCONDER NO ES AUTORIZAR.
 * ====================================================================
 *
 * Todo lo que decide este archivo es lo que se DIBUJA. La autorizacion la hace
 * el servidor en cada peticion, con las politicas del ADR 0005, y seguiria
 * haciendola igual si este archivo devolviera `true` en todo: un `fetch` a mano
 * desde la consola del navegador no pasa por aqui.
 *
 * Entonces para que existe: porque ofrecer un boton que va a responder 403 es
 * peor que no ofrecerlo. El usuario lo pulsa, no pasa nada que el entienda, y
 * aprende que la aplicacion falla. Un menu que solo ensena lo que se puede
 * hacer no es una medida de seguridad; es honestidad sobre los permisos que uno
 * tiene.
 *
 * Corolario practico: NO se escribe una guarda aqui y se deja el servidor sin
 * ella. Si algo solo esta protegido en este archivo, no esta protegido.
 */

export type Role = Schemas["UserRole"];
export type SessionUser = Schemas["SessionUser"];

export type Permissions = {
  /** Quien esta dentro, o `undefined` mientras la sesion no se ha leido. */
  user: SessionUser | undefined;
  role: Role | undefined;
  /** Usuarios, ajustes del sistema, respaldos y reinicio de fabrica. */
  isAdministrator: boolean;
  /** Mirar la bitacora de otro con `?owner=`: Administrador y Mentor. */
  canReadOthers: boolean;
  /**
   * Se esta mirando la bitacora de OTRO. Mientras tanto no se escribe: el
   * servidor escribe siempre en la del que firma la peticion, asi que un
   * "Nueva operacion" con la bitacora de otro delante registraria en la propia
   * lo que parecia ir a la ajena.
   */
  viewingOther: boolean;
  /**
   * Su propia bitacora: operaciones, notas y diario. El Mentor no escribe, y
   * nadie escribe mientras mira la de otro (ver `viewingOther`).
   */
  canWriteTrades: boolean;
  /** Catalogos, instrumentos, reglas y sugerencias. Solo el Administrador. */
  canWriteCatalogs: boolean;
};

/**
 * Sin sesion leida todavia, todo es `false`: mas vale un boton que aparece un
 * instante despues que uno que se ofrece y luego resulta que no era para ti.
 * En la practica no se ve, porque `RequireSession` no monta la aplicacion hasta
 * que la sesion esta resuelta.
 */
export function usePermissions(): Permissions {
  const session = useSession();
  const viewingOther = useOwnerId() !== undefined;
  const user = session.data?.user;
  const role = user?.role;

  return {
    user,
    role,
    isAdministrator: role === "administrator",
    canReadOthers: role === "administrator" || role === "mentor",
    viewingOther,
    canWriteTrades: (role === "administrator" || role === "trader") && !viewingOther,
    canWriteCatalogs: role === "administrator",
  };
}
